import { Reseller, Warrant } from '../../models';
import { makeAutoObservable, runInAction, observable } from 'mobx';
import apiManage from '../../request';
import i18n from '../../i18n';
import GlobalNotification from '../../component/common/snackbar';
import _ from 'lodash';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import { checkCardFromRule } from '../../component/card/card-from';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import { ResellerAccountCardArr } from './reseller-account.page';
import { toggleInObjectArray } from '../../constants/utils';


/**
 * 经销商账号信息详情数据管理
 */
export class ResellerAccountState implements ResellerInfoData {
    constructor(props: ResellerProps) {
        makeAutoObservable(this);
        this.belongedResellerId = props.belongedResellerId;
        this.defaultResellerInfo = props.defaultResellerInfo;
        this.defaultOperateMap = props.defaultOperateMap;
    }
    // 敏感操作页面的经销商信息
    defaultResellerInfo: Reseller | undefined;
    defaultOperateMap: Map<string, { origin: number, current: number }> | undefined;

    // 经销商_id
    belongedResellerId: string | undefined = '';
    //  经销商编辑信息
    resellerEditDetail: Reseller = {
        phone_prefix: '39',
        phone: '',
        first_name: '',
        last_name: '',
        /**
         * 0：male
         * 1：female
         */
        gender: '0',
        // 出生年月日
        birth: new Date,
        // 密码
        password: '',
        // *********************
        // Address
        // *********************
        // 具体地址
        local: '',
        // 城市
        city: '',
        // 省
        state: '',
        // 国家
        country: '',
        // 邮政编码
        postcode: '',
        // 门牌号
        home_number: '',
        create_date: new Date,
        // 经销商折扣
        reseller_discount: 0,
        // 余额
        balance: 0,
        // 公司名
        reg_name: '',
        // 税务地址
        reg_local: '',
        // 城市
        reg_city: '',
        // 省份
        reg_state: '',
        // 国家
        reg_country: '',
        // 邮政编码
        reg_postcode: '',
        // 增值税号
        reg_vat_number: '',
        // 电子发票收件人编码
        reg_des_code: '',
        // 法务联系邮箱
        reg_pec: '',
        // 税务注册号
        reg_fiscal_code: '',
        // 经销商头像
        avatar: ''
    };

    isEnableSkeleton: boolean = false;
    // 经销商源数据
    originResellerData: Reseller & { file?: File, originAvatar?: string } = {};
    // 编辑输入框操作map
    @observable operateMap: Map<string, { origin: number, current: number }> = new Map();
    // 输入框是否为空校验
    isEmptyVerify: boolean = false;
    // 输入框是否可以输入编辑
    isEditInput: boolean = false;
    // 保存按钮禁用
    disabledBtn: number = 0;
    // 允许空校验 - 输入框校验
    allowNullVerification: boolean = false;
    // 删除弹框配置
    deleteDialogConfig: {
        visible: boolean;
        title: string;
        content: string;
        forbidConfirm?: number;
        handleClick?: () => void | Promise<void>;
    } = {
            visible: false,
            title: '',
            content: '',
            forbidConfirm: 0
        };
    // 编辑经销商余额弹窗
    isOpenEditBalance: boolean = false;
    // 编辑经销商金额
    balance = '';
    // 经销商可销售授权点弹窗
    authPointDialog: AuthPointDialogProps = {
        visible: false,
        selectedList: [] as Warrant[],
    };
    // 经销商可销售授权点
    authPointList: Warrant[] = [];

    // *********************
    // Business Function
    // *********************

    // 初始化
    async init() {
        // admin端经销商详情、reseller端经销商详情、资源回收站经销商详情
        if (this.belongedResellerId) {
            // 获取账号数据
            await this.getResellerAccountData(this.belongedResellerId);
            // 关闭骨架屏
            runInAction(() => {
                this.isEnableSkeleton = false;
            })
        } else if (this.defaultResellerInfo) {
            // 敏感操作编辑经销商详情（操作时的经销商详情，不需要调api）
            this.resellerEditDetail = this.defaultResellerInfo;
            this.resellerEditDetail.balance = Number(this.resellerEditDetail.balance).toFixed(2) as any;
            if (this.defaultOperateMap) {
                this.operateMap = this.defaultOperateMap;
            }
            // 设置授权点信息
            const selectedList = (this.resellerEditDetail.resWarrant as Warrant[] || [])?.concat(this.resellerEditDetail.resWarrantCollection as Warrant[] || [])
            this.setAuthPointList(selectedList)
        } else {
            //  新增经销商
            this.initResellerData();
        }

    }


    /**
     * 获取经销商账号数据
     */
    async getResellerAccountData(resellerId?: string): Promise<void> {
        /**
         * 1.设置标记状态为禁用
         * 2.成功之后才可以编辑
         * 3.关闭骨架屏
         */
        try {
            const id = resellerId || this.belongedResellerId || '';
            const result = await apiManage.get_reseller_id_id(id);
            if (!result?.error) {
                let resellerLogo = '';
                if (result?._id) {
                    resellerLogo = await this.getResellerLogoUrl(result?._id);
                }
                // 数据组装保存
                const resellerInfoData: Reseller = {
                    phone_prefix: result.phone_prefix || '39',
                    phone: result.phone || '',
                    first_name: result.first_name || '',
                    last_name: result.last_name || '',
                    /**
                     * 0：male
                     * 1：female
                     * 2：others
                     */
                    gender: result.gender || '0',
                    // 出生年月日
                    birth: result.birth || new Date,
                    // 密码
                    password: result.password || '',
                    // *********************
                    // Address
                    // *********************
                    // 具体地址
                    local: result.local || '',
                    // 城市
                    city: result.city || '',
                    // 省
                    state: result.state || '',
                    // 国家
                    country: result.country || '',
                    // 邮政编码
                    postcode: result.postcode || '',
                    // 门牌号
                    home_number: result.home_number || '',
                    create_date: result.create_date || new Date,
                    // 经销商折扣
                    reseller_discount: result.reseller_discount || 0,
                    // 余额
                    balance: Number(result.balance || 0).toFixed(2) as any,
                    // 公司名
                    reg_name: result.reg_name || '',
                    // 税务地址
                    reg_local: result.reg_local || '',
                    // 城市
                    reg_city: result.reg_city || '',
                    // 省份
                    reg_state: result.reg_state || '',
                    // 国家
                    reg_country: result.reg_country || '',
                    // 邮政编码
                    reg_postcode: result.reg_postcode || '',
                    // 增值税号
                    reg_vat_number: result.reg_vat_number || '',
                    // 电子发票收件人编码
                    reg_des_code: result.reg_des_code || '',
                    // 法务联系邮箱
                    reg_pec: result.reg_pec || '',
                    // 税务注册号
                    reg_fiscal_code: result.reg_fiscal_code || '',
                    // 经销商头像
                    avatar: resellerLogo,
                    // 可销售授权点
                    resWarrant: result.resWarrant || [],
                    resWarrantCollection: result.resWarrantCollection || [],
                }
                // 源数据
                const originResellerData = Object.assign(result, { originAvatar: resellerLogo });
                // 设置界面数据
                this.setResellerAccountData(resellerInfoData, originResellerData);
                // 设置授权点信息
                const selectedList = (result.resWarrant as Warrant[] || [])?.concat(result.resWarrantCollection as Warrant[] || [])
                this.setAuthPointList(selectedList)
                this.setAuthPointDialog({
                    selectedList: selectedList,
                })
            } else {
                // 获取失败
                GlobalNotification.error(`${i18n.t('general_text_fetch_data_error')}`)
            }
        } catch (error) {
            GlobalNotification.error(`${i18n.t('general_text_fetch_data_error')}`)
        }
    }


    // 初始化弹窗 新建用
    initResellerData() {
        this.resellerEditDetail = {
            phone_prefix: '39',
            phone: '',
            first_name: '',
            last_name: '',
            /**
             * 0：male
             * 1：female
             * 2：others
             */
            gender: '0',
            // 出生年月日
            birth: new Date,
            // 密码
            password: '',
            // *********************
            // Address
            // *********************
            // 具体地址
            local: '',
            // 城市
            city: '',
            // 省
            state: '',
            // 国家
            country: '',
            // 邮政编码
            postcode: '',
            // 门牌号
            home_number: '',
            create_date: new Date,
            // 经销商折扣
            reseller_discount: 0,
            // 余额
            balance: 0,
            // 公司名
            reg_name: '',
            // 税务地址
            reg_local: '',
            // 城市
            reg_city: '',
            // 省份
            reg_state: '',
            // 国家
            reg_country: '',
            // 邮政编码
            reg_postcode: '',
            // 增值税号
            reg_vat_number: '',
            // 电子发票收件人编码
            reg_des_code: '',
            // 法务联系邮箱
            reg_pec: '',
            // 税务注册号
            reg_fiscal_code: '',
            // 经销商头像
            avatar: ''
        };
        this.originResellerData = Object.assign(this.resellerEditDetail, { originAvatar: '' });
        this.operateMap = new Map();
        this.setIsEmptyFlag(false);
    }

    /**
     * 经销商账号保存函数
     * @param saveType 保存类型: 新增保存 | 编辑保存
     * @returns
     */
    async onSave(saveType: 'ADD' | 'EDIT') {
        // 成功标识
        let successFlag: boolean = true;
        try {
            // 检查必填项  校验必填规则
            const errorList = checkCardFromRule({ config: ResellerAccountCardArr, data: this.resellerEditDetail as {} });
            this.setIsEmptyFlag(true);
            if (errorList?.length) {
                return;
            }
            // 源数据
            const originReseller = this.originResellerData;
            const { home_number, local, city, state, country, postcode,
                phone_prefix, phone, first_name, last_name, gender, birth, password, reseller_discount, balance,
                reg_name, reg_local, reg_city, reg_state, reg_country, reg_postcode, reg_vat_number, reg_des_code,
                reg_pec, reg_fiscal_code, avatar
            } = this.resellerEditDetail;

            // 可销售授权点 authPointList
            const resWarrant: string[] = [];
            const resWarrantCollection: string[] = [];
            this.authPointList.forEach((item) => {
                if (item.code?.includes('AUC')) {
                    resWarrantCollection.push(item.code || '')
                } else {
                    resWarrant.push(item.code || '')
                }
            })

            const params: Reseller = {
                // 主要信息 4个
                nickname: `${first_name} ${last_name}`,
                first_name,
                last_name,
                gender,
                birth: new Date(birth || new Date()),
                // 地址 6个
                local,
                city,
                state,
                country,
                postcode,
                home_number,
                // 联系方式 3个
                phone_prefix,
                phone,
                password,
                // 余额 2个
                reseller_discount: Number(reseller_discount),
                balance: Number(balance || 0),
                // 税务信息 10个
                reg_name,
                reg_vat_number,
                reg_des_code,
                reg_pec,
                reg_fiscal_code,
                reg_local,
                reg_city,
                reg_state,
                reg_country,
                reg_postcode,
                // 可销售授权点
                resWarrant,
                resWarrantCollection
            };
            // 新增操作
            if (saveType === 'ADD') {
                // 获取当前账户的信息
                const userInfo = LSM.get(LOCAL_STORAGE_KEYS.ACCOUNT);
                if (userInfo.account_type === 'RESELLER') {
                    _.set(params, 'belonged_reseller', userInfo._id);
                }
                // 请求接口
                const result = await apiManage.post_reseller(params);
                // 请求成功  TODO 这里的成功返回待进一步确认
                if (!result?.error) {
                    const { file } = this.originResellerData;
                    if (file && avatar !== '') {
                        const id = result?.upsertedId?._id || '';
                        const logoResult = await apiManage.patch_reseller_id_logo(id, file);
                        if (logoResult?.error) {
                            this.showNotificationMessage(i18n.t('general_text_failed_upload_picture'), 'error');
                        }
                    }
                    this.showNotificationMessage(i18n.t('global_text_operate_success'), 'success');
                    // 清空表单随后刷新数据
                    // this.initCreateGroupDialog();
                    // // 成功后刷新数据
                    // this.getGroupListData();
                } else {
                    successFlag = false;
                    if (result?.result?.code === 1008) {
                        // 检查返回的错误信息是否存在(根据提交给后端的信息去给后端判断是否存在)
                        this.checkReturnInfoIsExists(result);
                    } else if (result?.result?.message.includes('Resource Already Exists')) {
                        GlobalNotification.error(i18n.t('edit_account_auth_exist'));
                    } else this.showNotificationMessage(i18n.t('global_text_operate_failure'), 'error');
                }
            }
            // 编辑操作
            else {
                // 保存所用的参数
                // 密码有修改才需要传，余额不需要传
                const updateParams: Reseller = {
                    ...params,
                    _id: originReseller._id,
                    password: password || undefined,
                    reseller_discount: Number(reseller_discount || 0),
                    balance: undefined,
                }
                // 开启编辑禁用
                this.enableForbid();
                // 判断图片是否更改过
                if (avatar && avatar !== originReseller.originAvatar) {
                    const logoResult = await apiManage.patch_reseller_id_logo(originReseller._id!, originReseller.file ?? {} as File);
                    // 请求成功
                    if (logoResult?.error) {
                        this.showNotificationMessage(
                            `${i18n.t('person_page_message_avatar_upload_failed')}`,
                            'error'
                        );
                    } else {
                        this.showNotificationMessage(i18n.t('person_page_message_avatar_upload_successfully'), 'success');
                    }
                }
                // 大于1标识有改动 经销商修改
                if (this.checkFormIsEdited()) {
                    const result = await apiManage.patch_reseller_id_id_info(updateParams);
                    // 请求成功
                    if (!result?.error) {
                        this.showNotificationMessage(i18n.t('global_text_operate_success'), 'success')
                        // 刷新界面数据
                        this.getResellerAccountData(originReseller._id ?? '');
                    } else {
                        if (result?.result?.code === 1008) {
                            // 检查返回的错误信息是否存在(根据提交给后端的信息去给后端判断是否存在)
                            this.checkReturnInfoIsExists(result);
                        }
                        else this.showNotificationMessage(i18n.t('global_text_operate_failure'), 'error');
                    }
                }
                // 关闭编辑禁用
                this.closeForbid();
            }
            this.operateMap.clear();
            return successFlag
        } catch (error) {
            successFlag = false;
            this.operateMap.clear();
            this.showNotificationMessage(i18n.t('global_text_operate_failure'), 'error')
            return successFlag
        }
    }


    /**
     * 初始化密码
     */
    async initializePassword() {
        const result = await apiManage.post_reseller_id_password_initialize({ _id: this.originResellerData._id || '' });
        if ('error' in result) {
            GlobalNotification.error(i18n.t('ground_staff_component_text_initialization_failure'));
        } else {
            GlobalNotification.success(i18n.t('ground_staff_component_text_successful_initialization'));
        }
    }

    // 获取到经销商logo
    async getResellerLogoUrl(id: string): Promise<string> {
        let resellerImg = '';
        const result = await apiManage.get_reseller_id_id_logo(id);
        if (!result?.error) {
            resellerImg = result;
        }
        return resellerImg;
    }

    /**
     * 增加或者减少经销商余额
     * @param value 
     * @param key 
     */
    async onSaveBalance(type: string, balance: number | string) {
        try {
            const regular = BASE_CHECK_TYPE['price']?.regular;
            if (!regular.test(String(balance))) {
                return;
            }
            // 减少的余额大于本身余额
            if ((Number(balance || 0) - (this.resellerEditDetail.balance || 0) > 0) && type === 'DEDUCTION') {
                GlobalNotification.error(i18n.t('reseller_page_deduction_balance_error'));
                return
            }
            const originReseller = this.originResellerData;
            const params: Reseller & { type: string } = {
                _id: originReseller._id,
                type: type,
                balance: Number(balance || 0),
            }
            const result = await apiManage.patch_reseller_id_id_info(params);
            // 请求成功
            if (!result?.error) {
                this.showNotificationMessage(i18n.t('global_text_operate_success'), 'success');
                this.balance = '';
                // 刷新界面数据
                this.getResellerAccountData(originReseller._id ?? '');
            } else {
                if (result?.result?.code === 1008) {
                    // 检查返回的错误信息是否存在(根据提交给后端的信息去给后端判断是否存在)
                    this.checkReturnInfoIsExists(result);
                }
                else this.showNotificationMessage(i18n.t('global_text_operate_failure'), 'error');
            }
            this.setIsOpenBalanceDialog(false);
        } catch (error) {
            console.log('error', error)
        }

    }


    // 改变文本框输入
    handleChangeText(value: string, key: string): void {
        const curKeyVal = value;
        const originKeyVal = this.originResellerData[key as keyof Reseller];
        // 判断数据是否编辑过
        if (originKeyVal !== curKeyVal) {
            // _.set(this.groupEditDetail, [key], curKeyVal);
            this.operateMap.set(key, { origin: Number(originKeyVal), current: Number(curKeyVal) })
        }
        else this.operateMap.delete(key);
        _.set(this.resellerEditDetail, [key], curKeyVal);
    }

    /**
    * 检查后端错误信息返回对比当前填写信息是否存在重复
    * @param result 错误信息的返回
    */
    checkReturnInfoIsExists(errorResult: any) {
        /**
         * 1.根据后端返回的报错信息键值判断是否存在相同的集团或号码
         * 2.提示存在重复
         */
        // 查找后端返回哪一个键值重复了
        const key = (errorResult?.result?.details || '').match(/(\S*):/)[1];
        // 拿出对应国际化的信息的字段
        const existsField = this.getExistsInfo(key)[0];
        // 判断数据库是否存在相同的值  提示重复了
        _.includes(Object.keys(existsField), key) ?
            this.showNotificationMessage(`${i18n.t(existsField[key])}${i18n.t('general_text_already_existed')}`, 'error')
            : this.showNotificationMessage(`${errorResult?.result?.details || ''}`, 'error');
    }

    /**
     * 获取存在重复的信息
     * @param key 后端返回的对应的错误的key值
     * @returns
     */
    getExistsInfo(key: string) {
        const labelArr: any[] = [];
        ResellerAccountCardArr.forEach((item) => {
            item?.children && item?.children?.forEach((childItem) => {
                if (childItem.key === key && childItem.inputType === 'INPUT') {
                    labelArr.push({ [childItem.key]: childItem.label });
                }
            })
        });
        return labelArr;
    }

    // 校验表单是否修改
    checkFormIsEdited() {
        if (this.operateMap.size !== 0) {
            return true;
        } else {
            return false;
        }
    }

    // 开启输入框禁用和按钮禁用加载
    enableForbid() {
        this.disabledBtn = 1;
        this.isEditInput = true;
    }

    // 关闭输入框禁用和按钮禁用加载
    closeForbid() {
        this.disabledBtn = 0;
        this.isEditInput = false;
    }

    // 上传图片 更改头像文件
    async onChangeAvatar(files: File) {
        /**
         * 1.校验是否为图片以及是否存在
         * 2.对File类型转为url
         */
        const file = files;
        if (!file || !BASE_CHECK_TYPE['imageCheck'].regular.test(file?.type)) {
            GlobalNotification.error(`${i18n.t('general_message_image_type_incorrect_upload_failed')}`);
            return;
        } else {
            // 转为url链接
            const url = window.URL.createObjectURL(file);
            // 设置头像地址
            _.set(this.resellerEditDetail, 'avatar', url);
            // 设置头像文件
            this.originResellerData['file'] = file;
        }
    }

    /**
     * 打开选择授权弹窗
     * @returns 
     */
    handleOpenAuthPoint() {
        this.authPointDialog.visible = true
    }

    /**
     * 关闭授权点弹窗
     */
    handleCloseAuthPoint() {
        this.authPointDialog.visible = false
    }

    /**
     * 选择授权点
     * @param value 
     */
    handleCheckAuthPoint(value: Warrant) {
        const selectedList = toggleInObjectArray(this.authPointDialog.selectedList, value, 'code');
        this.authPointDialog.selectedList = selectedList;
    }

    /**
     * 删除授权点
     * @param value 
     */
    async handleDeleteAuthPoint(value: Warrant) {
        const selectedList = toggleInObjectArray(this.authPointDialog.selectedList, value, 'code');
        this.authPointDialog.selectedList = selectedList;
        await this.handleSaveAuthPoint()
        this.updateDeleteDialogConfig({
            visible: false,
            title: '',
            content: '',
        });
    }

    /**
     * 保存可销售的授权点
     */
    async handleSaveAuthPoint() {
        // 可销售授权点 authPointList
        const resWarrant: string[] = [];
        const resWarrantCollection: string[] = [];
        this.authPointDialog.selectedList.forEach((item) => {
            if (item.code?.includes('AUC')) {
                resWarrantCollection.push(item.code || '')
            } else {
                resWarrant.push(item.code || '')
            }
        })
        if (this.originResellerData._id) {
            // 编辑
            const result = await apiManage.patch_reseller_id_id_info({
                _id: this.originResellerData?._id,
                // 可销售授权点
                resWarrant,
                resWarrantCollection
            });
            if (result && !result.error) {
                this.showNotificationMessage(i18n.t('global_text_operate_success'), 'success')
                // 刷新界面数据
                await this.getResellerAccountData(this.resellerEditDetail._id ?? '');
            } else {
                this.showNotificationMessage(i18n.t('global_text_operate_failure'), 'error')
            }
        } else {
            // 新增
            this.authPointList = this.authPointDialog.selectedList
        }
        this.setAuthPointDialog({
            visible: false
        })
    }


    // 设置经销商账号界面数据
    setResellerAccountData(groupData: Reseller, originData: Reseller) {
        this.resellerEditDetail = groupData;
        // 设置源数据 用于比对数据是否修改
        this.originResellerData = originData;
    }

    // 设置为空标识
    setIsEmptyFlag(value: boolean) {
        this.isEmptyVerify = value;
    }

    /**
     * 更新删除弹窗
     * @param data
     */
    updateDeleteDialogConfig(data: typeof this.deleteDialogConfig) {
        this.deleteDialogConfig = data;
    }

    // 显示通知信息
    showNotificationMessage(message: string, type: 'success' | 'error') {
        type === 'success' ? GlobalNotification.success(message) : GlobalNotification.error(message);
    }

    // 显示编辑余额弹窗
    setIsOpenBalanceDialog(val: boolean) {
        this.isOpenEditBalance = val;
    }

    // 设置余额

    setBalance(val: string) {
        this.balance = val;
    }


    /**
     * 更新授权弹窗
     * @param value 
     */
    setAuthPointDialog(value: Partial<AuthPointDialogProps>) {
        this.authPointDialog = {
            ...this.authPointDialog,
            ...value
        };
    }


    /**
     * 授权列表
     */
    setAuthPointList(value: Warrant[]) {
        this.authPointList = value
    }

}

type ResellerProps = {
    // 经销商_id
    belongedResellerId?: string;
    // 敏感操作页面新增、编辑、修改经销商
    defaultResellerInfo?: Reseller;
    defaultOperateMap?: Map<string, { origin: number, current: number }>;

}

type ResellerInfoData = {
    // 经销商编辑信息
    resellerEditDetail: Reseller;
}

type AuthPointDialogProps = {
    visible: boolean;
    selectedList: Warrant[];
}